import { useEffect, useState, useRef } from "react";

const useNavbarLogic = () => {
  const [screenSize, setScreenSize] = useState("desktop");
  const [activeSection, setActiveSection] = useState("hero");
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // 📌 Atualiza o estado conforme a largura da tela
  useEffect(() => {
    const updateScreenSize = () => {
      if (window.innerWidth <= 768) {
        setScreenSize("mobile");
      } else if (window.innerWidth <= 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  // 📌 Fecha o menu ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  // 📌 Monitora a seção visível e atualiza a navbar
  useEffect(() => {
    const sectionIds = ["hero", "solutions", "portfolio"];
    const sections = sectionIds.map((id) => document.getElementById(id));

    const observerCallback = (entries) => {
      const visibleSection = entries.find((entry) => entry.isIntersecting);
      if (visibleSection) {
        setActiveSection(visibleSection.target.id);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "0px",
      threshold: 0.51,
    });

    sections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  // 📌 Scroll suave para uma seção específica
  const scrollToSection = (sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    if (!sectionElement) return;

    let offset = screenSize === 'mobile' ? 80 : 40;
    const width = window.innerWidth;

    // 📦 Ajustes específicos por seção (apenas mobile)
    if (screenSize === 'mobile') {
      if (sectionId === 'portfolio') {
        if (width === 375) {
          offset = 90; // iPhone SE
        } else if (width === 390) {
          offset = 120; // iPhone 13/14
        } else {
          offset = 140; // fallback mobile
        }
      }

      if (sectionId === 'solutions') {
        if (width === 375) {
          offset = 80; // iPhone SE
        } else if (width === 390) {
          offset = 80; // iPhone 13/14
        } else {
          offset = 110; // fallback mobile
        }
      }
    }

    const y = sectionElement.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({ top: y, behavior: "smooth" });

    setTimeout(() => {
      setActiveSection(sectionId);
    }, 300);
  };

  // 📌 Scroll para a seção de contato
  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    if (!contactSection) return;
  
    let offset = 20;

    const width = Math.round(window.screen.width);
    const height = Math.round(window.screen.height);

    if (screenSize === 'mobile') {
      if (width === 375 && height === 667) {
        offset = 90; // iPhone SE (2ª geração)
      } else if (width === 390 && height === 844) {
        offset = 40; // iPhone 13/14
      } else if (width === 430 && height === 932) {
        offset = 100; // iPhone 14/15 Pro Max
      } else if (width === 412 && height === 915) {
        offset = 40; // Pixel 6 ou Android médio
      } else {
        offset = 100; // fallback para outros iPhones/Androids
      }
    }
  
    const y = contactSection.getBoundingClientRect().top + window.pageYOffset - offset;
  
    window.scrollTo({ top: y, behavior: "smooth" });
  
    setIsMobileMenuOpen(false); // Fecha o menu no mobile
  };  

  return {
    screenSize,
    activeSection,
    isHovered,
    setIsHovered,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    menuRef,
    buttonRef,
    scrollToSection,
    scrollToContact,
  };
};

export default useNavbarLogic;
